import { useCallback, useMemo, useRef } from 'react';

import { MEDIA_SM, useMediaQuery } from 'approot/shared/style';
import ActivitySheet from '../../shared/sheet/activity-sheet';
import SubActivityView from '../../sub-activity/sub-activity-view';
import {
  useInteractiveAudio,
  useInteractiveModal,
} from '../shared/interactive-response-shared.hooks';
import { InteractiveResponseContainer } from '../interactive-response.style';
import {
  RESPONSE_FREE,
  ViewProps,
} from './interactive-response-categorise.constants';
import InteractiveResponseComponentResponse from '../shared/interactive-response-component-response';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import InteractiveResponseCategoriseSmallView from './interactive-response-categorise-small-view';
import { getComponentSelectorId } from '../../shared/logic/get-component-selector-id';
import InteractiveResponseCategoriseLargeView from './interactive-response-categorise-large-view';
import ClassroomAnswerTabs, {
  CLASS_VIEW,
  INDIVIDUAL_VIEW,
  QUESTION_VIEW,
  SUGGESTED_VIEW,
} from 'approot/activity-builder/classroom/classroom-answer-tabs';
import InteractiveResponseClassResponses from 'approot/activity-builder/classroom/interactive-response/interactive-response-class-responses';
import InteractiveResponseIndividualResponses from 'approot/activity-builder/classroom/interactive-response/interactive-response-individual-responses';
import { useActivityBuilderFlags } from 'approot/activity-builder/activity-builder-flags';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import { useTeacherGetAnswers } from 'approot/activity-builder/present/activity-answers.hooks';
import { useBatchAnswersContext } from 'approot/activity-builder/components/shared/batch-answers.context';
import useInitialClassroomTab from 'approot/activity-builder/classroom/use-initial-classroom-tab';
import { DropItem } from 'approot/activity-builder/components/shared/interactive-drag-drop/interactive-drag-drop.constants';
import customNanoId from 'lib/nanoid/nanoid';
import InteractiveResponseCategorisePreviewAnswer from './play/interactive-response-categorise-preview-answers';
import {
  prepareItemsForSave,
  prepareSmallItemsForSave,
} from './interactive-response-categorise.utils';
import { DropItem as DropItemSmall } from './play/interactive-response-categorise-small.constants';
import InteractiveResponseSuggestedAnswers from 'approot/activity-builder/classroom/interactive-response/interactive-response-suggested-answers';
import type { InteractiveResponseAnswerFragment } from '../../../present/activity-answers.graphql.generated';
import { hasNoRichTextData } from '../../suggested-answers/view/suggested-answer-view.utils';
import ClassroomAwaitingResponse from 'approot/activity-builder/classroom/classroom-awaiting';
import {
  ActivitySheetGridMax,
  ActivitySheetInnerContent,
  ActivitySheetInnerMax,
} from '../../shared/sheet/activity-sheet.style';
import { getSubActivityInstructionAsText } from '../../sub-activity/sub-activity.utils';
import InteractiveResponseCategoriseAnswers from './interactive-response-categorise-answers';
import ActivitySheetSaveStatus from '../../shared/sheet/activity-sheet-save-status';

const InteractiveResponseCategoriseView = (props: ViewProps) => {
  const { id, subActivityProperties, suggestedAnswerProperties, properties } =
    props;

  const isMediaUpSm = useMediaQuery(MEDIA_SM);
  const { isPlaying, startGame, finishGame } = useInteractiveModal();
  const { isVisible, openModal, closeModal } = useSimpleModalState();
  const { activityBuilderRespondFlag } = useActivityBuilderFlags();
  const { isPublished, isLesson } = useActivityBuilderPresentContext();

  const closeModalCallbackRef = useRef<Function | undefined>(undefined);

  const canRespond = isPublished && activityBuilderRespondFlag;
  const { tab, setTab } = useInitialClassroomTab({
    isVisible,
    canRespond,
    componentId: id,
  });

  const { toggleAudio, playAudio, playClick, playStart } =
    useInteractiveAudio();
  const isFreeResponse =
    properties.interactiveResponseCategorise.answerType === RESPONSE_FREE;

  const { componentAnswers, variables, hasFetchedAnswers, error } =
    useTeacherGetAnswers({
      componentId: id,
      skip: !activityBuilderRespondFlag,
    });

  const { handleUpdate } = useBatchAnswersContext();

  const showSuggestedAnswers = useMemo(
    () => !isFreeResponse && !hasNoRichTextData(suggestedAnswerProperties),
    [isFreeResponse, suggestedAnswerProperties]
  );

  const handleSaveAttempt = useCallback(
    (dropItems: DropItem[]) => {
      if (
        isPublished &&
        activityBuilderRespondFlag &&
        subActivityProperties?.activityComponentId &&
        variables
      ) {
        handleUpdate({
          variables,
          input: {
            __typename: 'ActivityAnswer',
            uuid: customNanoId(),
            activityId: subActivityProperties?.activityComponentId,
            componentId: id,
            createdAt: new Date(),
            updatedAt: null,
            studentId: null,
            answerReferences: null,
            answerType: 'interactive-response-answer',
            answer: {
              __typename: 'InteractiveResponseAnswer',
              interactiveResponseItems: prepareItemsForSave(
                dropItems,
                properties.interactiveResponseCategorise.items
              ),
            },
          },
        });
      }
    },
    [
      id,
      subActivityProperties,
      variables,
      activityBuilderRespondFlag,
      isPublished,
      handleUpdate,
      properties,
    ]
  );

  const handleSmallSaveAttempt = useCallback(
    (dropItems: DropItemSmall[]) => {
      if (
        isPublished &&
        activityBuilderRespondFlag &&
        subActivityProperties?.activityComponentId &&
        variables
      ) {
        handleUpdate({
          variables,
          input: {
            __typename: 'ActivityAnswer',
            uuid: customNanoId(),
            activityId: subActivityProperties?.activityComponentId,
            componentId: id,
            createdAt: new Date(),
            updatedAt: null,
            studentId: null,
            answerReferences: null,
            answerType: 'interactive-response-answer',
            answer: {
              __typename: 'InteractiveResponseAnswer',
              interactiveResponseItems: prepareSmallItemsForSave(dropItems),
            },
          },
        });
      }
    },
    [
      id,
      subActivityProperties,
      variables,
      activityBuilderRespondFlag,
      isPublished,
      handleUpdate,
    ]
  );

  const handleClose = useCallback(() => {
    if (closeModalCallbackRef.current) {
      closeModalCallbackRef.current();
      closeModalCallbackRef.current = undefined;
    } else {
      closeModal();
    }
  }, [closeModal]);

  return (
    <>
      <SubActivityView subActivityDetails={props.subActivityProperties}>
        <InteractiveResponseContainer id={getComponentSelectorId(props.id)}>
          <InteractiveResponseCategoriseAnswers
            {...props}
            answerAttempt={componentAnswers?.[0]}
            isLoading={canRespond && !hasFetchedAnswers}
          />
          <InteractiveResponseComponentResponse
            id={id}
            isLoggedIn={isPublished && activityBuilderRespondFlag}
            hasFetchedAnswers={hasFetchedAnswers}
            error={error}
            label="Grouping"
            title={
              getSubActivityInstructionAsText(
                subActivityProperties?.instructions
              ) || properties.interactiveResponseCategorise.title
            }
            onStart={() => {
              openModal();
              startGame();
            }}
          />
        </InteractiveResponseContainer>
      </SubActivityView>
      {isVisible && (
        <ActivitySheet
          isVisible
          title={props.properties.interactiveResponseCategorise.title || ''}
          hasBackgroundColor={tab === QUESTION_VIEW}
          customActions={
            <ActivitySheetSaveStatus
              showStatus={tab === QUESTION_VIEW}
              title="Interactive response"
              onClose={handleClose}
            />
          }
          tabsContent={
            <ClassroomAnswerTabs
              isPublished={canRespond}
              includeIndividualAnswers
              includeSuggestedAnswers={showSuggestedAnswers}
              includeClassResponse={!isFreeResponse}
              tab={tab}
              onSelect={option => setTab(option)}
              componentId={id}
              onCloseActivitySheet={handleClose}
            />
          }
        >
          {tab === QUESTION_VIEW && (
            <>
              {isMediaUpSm ? (
                <InteractiveResponseCategoriseLargeView
                  {...props}
                  isPlaying={isPlaying}
                  finishGame={finishGame}
                  onAnswerAttempt={handleSaveAttempt}
                  attemptAnswers={
                    componentAnswers?.[0]
                      ?.answer as InteractiveResponseAnswerFragment
                  }
                  setCloseModalCallback={callback => {
                    closeModalCallbackRef.current = callback;
                  }}
                  showAnswer={isLesson}
                />
              ) : (
                <InteractiveResponseCategoriseSmallView
                  {...props}
                  isPlaying={isPlaying}
                  finishGame={finishGame}
                  playStart={playStart}
                  playClick={playClick}
                  playAudio={playAudio}
                  toggleAudio={toggleAudio}
                  onAnswerAttempt={handleSmallSaveAttempt}
                  attemptAnswers={
                    componentAnswers?.[0]
                      ?.answer as InteractiveResponseAnswerFragment
                  }
                  showAnswer={isLesson}
                />
              )}
            </>
          )}
          {tab === CLASS_VIEW && (
            <ActivitySheetInnerContent id="content">
              <ActivitySheetInnerMax>
                <InteractiveResponseClassResponses
                  {...props}
                  componentId={id}
                />
              </ActivitySheetInnerMax>
            </ActivitySheetInnerContent>
          )}
          {tab === INDIVIDUAL_VIEW && (
            <ActivitySheetInnerContent id="content">
              <ActivitySheetGridMax>
                <InteractiveResponseIndividualResponses
                  componentId={id}
                  component={props}
                  renderAttempt={({ selectedAnswers }) =>
                    selectedAnswers ? (
                      <InteractiveResponseCategorisePreviewAnswer
                        {...props}
                        attemptAnswers={selectedAnswers}
                        showAnswer
                      />
                    ) : (
                      <ClassroomAwaitingResponse />
                    )
                  }
                />
              </ActivitySheetGridMax>
            </ActivitySheetInnerContent>
          )}
          {tab === SUGGESTED_VIEW && (
            <ActivitySheetInnerContent id="content">
              <ActivitySheetInnerMax>
                <InteractiveResponseSuggestedAnswers
                  {...props}
                  component={props}
                />
              </ActivitySheetInnerMax>
            </ActivitySheetInnerContent>
          )}
        </ActivitySheet>
      )}
    </>
  );
};
export default InteractiveResponseCategoriseView;
